
import React from 'react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import CustomerInformation, { CustomerFormValues } from './CustomerInformation';
import ShippingInformation, { ShippingFormValues } from './ShippingInformation';
import PaymentMethod from './PaymentMethod';
import OrderSummary from './OrderSummary';
import { CartItem } from '@/store/useCartStore';
import { ShippingOptionType } from './ShippingInformation';
import { PaymentMethodType } from './PaymentMethod';
import { useIsMobile } from '@/hooks/use-mobile';
import { ScrollArea } from '@/components/ui/scroll-area';

interface MobileCheckoutTabsProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  shippingOptions: ShippingOptionType[];
  selectedShipping: string;
  setShipping: (id: string) => void;
  paymentMethods: PaymentMethodType[];
  selectedPayment: string;
  setPaymentMethod: (id: string) => void;
  items: CartItem[];
  subtotal: number;
  shippingCost: number;
  currency: string;
  onSubmit: () => void;
  isSubmitting: boolean;
  handleCustomerInfoSubmit: (data: CustomerFormValues) => void;
  handleShippingInfoSubmit: (data: ShippingFormValues) => void;
}

const MobileCheckoutTabs: React.FC<MobileCheckoutTabsProps> = ({
  activeTab,
  setActiveTab,
  shippingOptions,
  selectedShipping,
  setShipping,
  paymentMethods,
  selectedPayment,
  setPaymentMethod,
  items,
  subtotal,
  shippingCost,
  currency,
  onSubmit,
  isSubmitting,
  handleCustomerInfoSubmit,
  handleShippingInfoSubmit
}) => {
  const isMobile = useIsMobile();
  
  const handleNextTab = (current: string) => {
    if (current === "customer") setActiveTab("shipping");
    else if (current === "shipping") setActiveTab("payment");
  };

  const handlePrevTab = (current: string) => {
    if (current === "shipping") setActiveTab("customer");
    else if (current === "payment") setActiveTab("shipping");
  };

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full mb-6">
      <TabsList className="grid grid-cols-3 w-full">
        <TabsTrigger value="customer">Customer</TabsTrigger>
        <TabsTrigger value="shipping">Shipping</TabsTrigger>
        <TabsTrigger value="payment">Payment</TabsTrigger>
      </TabsList>

      <ScrollArea className="max-h-[calc(100vh-220px)]">
        <TabsContent value="customer" className="mt-4">
          <CustomerInformation onSubmit={(data) => {
            handleCustomerInfoSubmit(data);
            handleNextTab("customer");
          }} />
          <div className="flex justify-end mt-4">
            <Button onClick={() => handleNextTab("customer")} className="bg-ahavex-blue hover:bg-ahavex-darkblue text-white">
              Next: Shipping
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="shipping" className="mt-4">
          <ShippingInformation 
            shippingOptions={shippingOptions} 
            selectedShipping={selectedShipping} 
            setShipping={setShipping} 
            currency={currency} 
            onSubmit={(data) => {
              handleShippingInfoSubmit(data);
              handleNextTab("shipping");
            }}
          />
          <div className="flex justify-between mt-4">
            <Button variant="outline" onClick={() => handlePrevTab("shipping")}>
              Back
            </Button>
            <Button onClick={() => handleNextTab("shipping")} className="bg-ahavex-blue hover:bg-ahavex-darkblue text-white">
              Next: Payment
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="payment" className="mt-4">
          <PaymentMethod 
            paymentMethods={paymentMethods} 
            selectedPayment={selectedPayment} 
            setPaymentMethod={setPaymentMethod} 
          />
          <div className="flex justify-between mt-4">
            <Button variant="outline" onClick={() => handlePrevTab("payment")}>
              Back
            </Button>
          </div>

          {/* Order Summary for Mobile - Only shown in mobile on payment tab */}
          {isMobile && (
            <div className="mt-6 lg:hidden">
              <OrderSummary 
                items={items} 
                subtotal={subtotal} 
                shippingCost={shippingCost} 
                currency={currency} 
                onSubmit={onSubmit} 
                isSubmitting={isSubmitting} 
              />
            </div>
          )}
        </TabsContent>
      </ScrollArea>
    </Tabs>
  );
};

export default MobileCheckoutTabs;
